/**
 * B20b(h) — the storefront side of the GUEST DOOR. A buyer with no
 * credential mints a short-lived CUSTOMER-scoped gst1 token via the
 * public `session.guest` operation; from the next call on they are an
 * ordinary authenticated CUSTOMER (Inv 10 walls unchanged).
 *
 * The gate is the ONE place minting happens: concurrent callers share the
 * in-flight mint (one audit row, not N), the deadline is stored with a
 * safety skew so an in-flight call never straddles expiry, and a failed
 * mint leaves the session untouched and rethrows — the container reports
 * it, nothing pretends to be signed in.
 */

import type { ApiClient } from "./api.js";
import type { Session } from "./session.js";
import type { ChannelThemeLike } from "./theme.js";

/** Re-mint this long before the signed deadline (in-flight calls must not straddle it). */
export const GUEST_EXPIRY_SKEW_MS = 30_000;

export interface GuestGate {
  /** Returns a live token, minting one first if needed. */
  ensure(): Promise<string>;
}

export function createGuestGate(options: {
  readonly api: ApiClient;
  readonly session: Session;
  readonly channelId: string;
  /** B52 — called when a mint response carries the channel's brand theme. */
  readonly onTheme?: (theme: ChannelThemeLike) => void;
  /** B397 leg (3) — the channel's publishable key rides the same mint. */
  readonly onPublishableKey?: (publishableKey: string) => void;
  /** B91 GAP-6 — the tenant's onboarded connected account, when the mint carries one. */
  readonly onStripeAccount?: (stripeAccount: string) => void;
  /** B403 #26 — the tenant's stored business name rides the same mint. */
  readonly onBusinessName?: (businessName: string) => void;
  /** B403 #22-extension — the tenant's stored type rides the same mint (a closed-set word; the caller validates). */
  readonly onTenantType?: (tenantType: string) => void;
  /** B524 leg 2 — the tenant's GTM container rides the same mint (the HOSTED page loads it). */
  readonly onGtmContainerId?: (containerId: string) => void;
  /** B549 — the channel's effective group threshold rides the same mint (absent = off). */
  readonly onGroupThreshold?: (threshold: number) => void;
  /** B549 (9) — the groups desk's first name rides beside the threshold. */
  readonly onGroupsContactName?: (name: string) => void;
}): GuestGate {
  let inFlight: Promise<string> | undefined;

  const mint = async (): Promise<string> => {
    try {
      const res = (await options.api.call("session.guest", { channelId: options.channelId })) as {
        token: string;
        customerId: string;
        expiresAt: number;
        theme?: ChannelThemeLike;
        publishableKey?: string;
        stripeAccount?: string;
        businessName?: string;
        tenantType?: string;
        gtmContainerId?: string;
        groupThreshold?: number;
        groupsContactName?: string;
      };
      options.session.setToken(res.token, res.expiresAt - GUEST_EXPIRY_SKEW_MS);
      if (res.theme !== undefined) options.onTheme?.(res.theme);
      if (res.publishableKey !== undefined) options.onPublishableKey?.(res.publishableKey);
      if (res.stripeAccount !== undefined) options.onStripeAccount?.(res.stripeAccount);
      if (res.businessName !== undefined) options.onBusinessName?.(res.businessName);
      if (res.tenantType !== undefined) options.onTenantType?.(res.tenantType);
      if (res.gtmContainerId !== undefined) options.onGtmContainerId?.(res.gtmContainerId);
      if (res.groupThreshold !== undefined) options.onGroupThreshold?.(res.groupThreshold);
      if (res.groupsContactName !== undefined) options.onGroupsContactName?.(res.groupsContactName);
      return res.token;
    } finally {
      inFlight = undefined;
    }
  };

  return {
    ensure: () => {
      const live = options.session.token();
      if (live !== undefined) return Promise.resolve(live);
      inFlight ??= mint();
      return inFlight;
    },
  };
}
